import { useState } from "react";
import {
  opsReports,
  reportTypeLabels,
  reportStatusLabels,
  type OpsReport,
} from "@/mocks/opsReports";

function ReportsPage() {
  const [activeType, setActiveType] = useState<string>("全部");
  const [selectedReport, setSelectedReport] = useState<OpsReport | null>(null);

  const typeFilter = ["全部", "daily", "weekly", "monthly", "incident"];

  const filteredReports = opsReports.filter((r) => {
    if (activeType === "全部") return true;
    return r.type === activeType;
  });

  const statusIcon = (status: string) => {
    switch (status) {
      case "generated": return "ri-check-double-line text-emerald-500";
      case "pending": return "ri-loader-4-line text-amber-500 animate-spin";
      case "archived": return "ri-archive-line text-gray-400";
      default: return "ri-file-line text-gray-400";
    }
  };

  const typeIcon = (type: string) => {
    switch (type) {
      case "daily": return "ri-calendar-line";
      case "weekly": return "ri-calendar-check-line";
      case "monthly": return "ri-calendar-2-line";
      case "incident": return "ri-alert-line text-red-500";
      default: return "ri-file-line";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">运维报告</h1>
          <p className="text-sm text-gray-500 mt-1">日报、周报、月报及事件分析报告</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
            <i className="ri-download-2-line"></i>
            导出全部
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap">
            <i className="ri-add-line"></i>
            生成报告
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 no-print">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">日报</div>
          <div className="text-xl font-bold text-gray-900">{opsReports.filter((r) => r.type === "daily").length}</div>
          <div className="text-xs text-emerald-500 mt-1">已生成</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">周报</div>
          <div className="text-xl font-bold text-gray-900">{opsReports.filter((r) => r.type === "weekly").length}</div>
          <div className="text-xs text-emerald-500 mt-1">已生成</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">月报</div>
          <div className="text-xl font-bold text-gray-900">{opsReports.filter((r) => r.type === "monthly").length}</div>
          <div className="text-xs text-emerald-500 mt-1">已生成</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">事件报告</div>
          <div className="text-xl font-bold text-gray-900">{opsReports.filter((r) => r.type === "incident").length}</div>
          <div className="text-xs text-red-400 mt-1">已记录</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">归档报告</div>
          <div className="text-xl font-bold text-gray-900">{opsReports.filter((r) => r.status === "archived").length}</div>
          <div className="text-xs text-gray-400 mt-1">历史存档</div>
        </div>
      </div>

      {/* Type Filter */}
      <div className="flex items-center gap-2 no-print">
        {typeFilter.map((type) => (
          <button
            key={type}
            onClick={() => setActiveType(type)}
            className={`px-3.5 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
              activeType === type
                ? "bg-teal-500 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {type === "全部" ? type : reportTypeLabels[type]}
          </button>
        ))}
      </div>

      {/* Report List */}
      <div className="space-y-3 no-print">
        {filteredReports.map((report) => (
          <button
            key={report.id}
            onClick={() => setSelectedReport(report)}
            className={`w-full bg-white rounded-lg border p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${
              selectedReport?.id === report.id
                ? "border-teal-300 ring-1 ring-teal-200"
                : "border-gray-200"
            }`}
          >
            <div className="flex items-center gap-4">
              {/* Type Icon */}
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                report.type === "incident" ? "bg-red-50" : "bg-teal-50"
              }`}>
                <i className={`${typeIcon(report.type)} text-lg ${report.type === "incident" ? "text-red-500" : "text-teal-600"}`}></i>
              </div>

              {/* Main Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold text-gray-900">{report.title}</h3>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                    report.type === "incident"
                      ? "bg-red-50 text-red-600"
                      : "bg-teal-50 text-teal-600"
                  }`}>
                    {reportTypeLabels[report.type]}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                    report.status === "generated" ? "bg-emerald-50 text-emerald-600" :
                    report.status === "pending" ? "bg-amber-50 text-amber-600" :
                    "bg-gray-100 text-gray-500"
                  }`}>
                    <i className={`${statusIcon(report.status)} text-[10px] mr-0.5`}></i>
                    {reportStatusLabels[report.status]}
                  </span>
                </div>
                <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{report.summary}</p>
              </div>

              {/* Right Meta */}
              <div className="flex-shrink-0 flex flex-col items-end gap-1">
                <span className="text-xs text-gray-400">{report.period}</span>
                <span className="text-xs text-gray-400">{report.generatedAt}</span>
                <span className="text-xs text-gray-400">{report.fileSize}</span>
              </div>
            </div>

            {/* KPI Mini Row (only for non-incident reports) */}
            {report.type !== "incident" && (
              <div className="flex items-center gap-5 mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">在线率</span>
                  <span className="text-xs font-semibold text-emerald-600">{report.deviceOnlineRate}%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">船次</span>
                  <span className="text-xs font-semibold text-gray-700">{report.totalTours}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">游客</span>
                  <span className="text-xs font-semibold text-gray-700">{report.totalVisitors.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">故障</span>
                  <span className={`text-xs font-semibold ${report.incidentsCount > 5 ? "text-red-500" : "text-gray-700"}`}>
                    {report.incidentsCount}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">电池健康</span>
                  <span className={`text-xs font-semibold ${report.avgBatteryHealth < 90 ? "text-amber-600" : "text-emerald-600"}`}>
                    {report.avgBatteryHealth}%
                  </span>
                </div>
              </div>
            )}
          </button>
        ))}

        {filteredReports.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-file-paper-line text-4xl block mb-3"></i>
            暂无此类型的运维报告
          </div>
        )}
      </div>

      {/* Report Detail Drawer */}
      {selectedReport && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40 no-print"
            onClick={() => setSelectedReport(null)}
          ></div>
          <div className="fixed top-0 right-0 h-full w-full md:w-[580px] bg-white z-50 shadow-2xl animate-[slideIn_0.3s_ease-out] overflow-y-auto print-report-container">
            {/* Print-only Logo Header */}
            <div className="print-only px-6 pt-6 pb-3 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
                  <i className="ri-glasses-line text-white text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">三峡文旅 · 智慧眼镜运营中心</div>
                  <div className="text-xs text-gray-500">Smart Glasses O&M Center · Three Gorges Tourism</div>
                </div>
              </div>
            </div>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                  selectedReport.type === "incident" ? "bg-red-50" : "bg-teal-50"
                }`}>
                  <i className={`${typeIcon(selectedReport.type)} text-lg ${selectedReport.type === "incident" ? "text-red-500" : "text-teal-600"}`}></i>
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">{selectedReport.title}</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                      selectedReport.type === "incident" ? "bg-red-50 text-red-600" : "bg-teal-50 text-teal-600"
                    }`}>
                      {reportTypeLabels[selectedReport.type]}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                      selectedReport.status === "generated" ? "bg-emerald-50 text-emerald-600" :
                      selectedReport.status === "pending" ? "bg-amber-50 text-amber-600" :
                      "bg-gray-100 text-gray-500"
                    }`}>
                      {reportStatusLabels[selectedReport.status]}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedReport(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 no-print"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Meta Info */}
            <div className="px-6 py-4 border-b border-gray-100 grid grid-cols-2 gap-3">
              <div>
                <span className="text-xs text-gray-400">报告周期</span>
                <p className="text-sm text-gray-900 font-medium">{selectedReport.period}</p>
              </div>
              <div>
                <span className="text-xs text-gray-400">生成时间</span>
                <p className="text-sm text-gray-900">{selectedReport.generatedAt}</p>
              </div>
              <div>
                <span className="text-xs text-gray-400">作者</span>
                <p className="text-sm text-gray-900">{selectedReport.author}</p>
              </div>
              <div>
                <span className="text-xs text-gray-400">文件大小</span>
                <p className="text-sm text-gray-900">{selectedReport.fileSize}</p>
              </div>
            </div>

            {/* KPI Section (non-incident) */}
            {selectedReport.type !== "incident" && (
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3">关键指标</h3>
                <div className="grid grid-cols-5 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-emerald-600">{selectedReport.deviceOnlineRate}%</div>
                    <div className="text-xs text-gray-400 mt-0.5">在线率</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-gray-900">{selectedReport.totalTours}</div>
                    <div className="text-xs text-gray-400 mt-0.5">船次</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-gray-900">{selectedReport.totalVisitors.toLocaleString()}</div>
                    <div className="text-xs text-gray-400 mt-0.5">游客</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className={`text-lg font-bold ${selectedReport.incidentsCount > 5 ? "text-red-500" : "text-gray-900"}`}>
                      {selectedReport.incidentsCount}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">故障数</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className={`text-lg font-bold ${selectedReport.avgBatteryHealth < 90 ? "text-amber-500" : "text-emerald-600"}`}>
                      {selectedReport.avgBatteryHealth}%
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">电池健康</div>
                  </div>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="px-6 py-5">
              <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3">报告摘要</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{selectedReport.summary}</p>
            </div>

            {/* Print-only Copyright Footer */}
            <div className="print-only px-6 py-4 border-t border-gray-200 mt-4">
              <p className="text-xs text-gray-400 text-center">
                © 2026 三峡文旅智慧眼镜运营中心 · 内部文档 · 保密
              </p>
              <p className="text-[10px] text-gray-300 text-center mt-1">
                本报告由运维管理平台自动生成 · {new Date().toLocaleDateString("zh-CN")}
              </p>
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between no-print">
              <div className="flex items-center gap-2">
                <button className="flex items-center gap-1.5 px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap">
                  <i className="ri-download-2-line"></i>
                  下载报告
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 px-4 py-2 border border-teal-200 text-teal-600 text-sm font-medium rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-file-pdf-line"></i>
                  导出PDF
                </button>
                {selectedReport.status === "generated" && (
                  <button className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
                    <i className="ri-archive-line"></i>
                    归档
                  </button>
                )}
              </div>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-sm text-gray-400 hover:text-gray-600"
              >
                关闭
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ReportsPage;